import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import useAuth from "../hooks/useAuth";
import SEO from "../components/common/SEO";
import OrganizationSchema from "../components/common/OrganizationSchema";
import HeroSection from "../components/home/HeroSection";
import PartnersSection from "../components/home/PartnersSection";
import HowItWorks from "../components/home/HowItWorks";
import FeatureSections from "../components/home/FeatureSections";
import HowItWorksVideoButton from "../components/home/HowItWorksVideoButton";
import DidYouKnow from "../components/home/DidYouKnow";
import ReviewsSection from "../components/home/ReviewsSection";
import PopularProjects from "../components/home/PopularProjects";
import OptimizedImage from "../components/common/OptimizedImage";
import {
  trackCallSignupClicked,
  trackContractorSignupStarted,
  trackCTAClicked,
  trackSeoLandingViewed,
} from "../utils/analytics";
import "../styles/Home.css";

const ContractorSignUpSection = ({ onSignUpContractorClick, onCallSignupClick }) => (
  <div className="contractor-signup-container">
    <div className="contractor-signup-content">
      <div className="contractor-signup-text">
        <h2 className="contractor-signup-title">
          Plumber in Leeds? Electrician in Bristol? <span className="highlight-text">Help homeowners find you.</span>
        </h2>
        <p className="contractor-signup-description">
          Create a profile so homeowners can find your business and send project
          requests. Review their brief, discuss the work and provide your own quote.
          Enquiries depend on your trade, area and homeowner demand.
        </p>
        <div className="contractor-buttons-container">
          <button
            className="button button-primary"
            onClick={onSignUpContractorClick}
          >
            View Packages
          </button>
          <Link
            to="/for-tradespeople?source=homepage_contractor_cta"
            className="button button-secondary contractor-learn-more-btn"
          >
            Learn More
          </Link>
          <a
            href="tel:01296325153"
            className="button call-now-btn-contractor"
            onClick={onCallSignupClick}
          >
            Or Call to Sign Up
          </a>
        </div>
      </div>
      <div className="contractor-signup-image">
        <OptimizedImage
          src="/images/WavingPenguinContractor.jpg"
          alt="Contractor sign up"
          className="contractor-signup-image-content"
          width={400}
          height={400}
          loading="lazy"
        />
      </div>
    </div>
  </div>
);

const Home = ({ reviews = [], isDataLoading, isReviewsLoading = isDataLoading, reviewsError = false, retryReviews }) => {
  const navigate = useNavigate();
  const { user, profileLoading } = useAuth();
  const [currentReviewIndex, setCurrentReviewIndex] = useState(0);

  const safeReviews = useMemo(() => {
    if (Array.isArray(reviews)) return reviews;
    if (typeof reviews === "string") {
      try {
        const parsed = JSON.parse(reviews);
        if (Array.isArray(parsed)) return parsed;
        return parsed ? [parsed] : [];
      } catch (err) {
        console.warn("Home: failed to JSON.parse reviews string:", err);
        return [];
      }
    }
    if (reviews && typeof reviews === "object") {
      if (Array.isArray(reviews.data)) return reviews.data;
      if (Array.isArray(reviews.items)) return reviews.items;
      const firstArray = Object.values(reviews).find(Array.isArray);
      if (firstArray) return firstArray;
    }
    if (reviews !== undefined && reviews !== null) {
      console.warn(
        "Home: unexpected `reviews` shape — expected array but got:",
        reviews
      );
    }
    return [];
  }, [reviews]);

  const highRatedReviews = useMemo(
    () => safeReviews.filter((review) => review && review.stars >= 3),
    [safeReviews]
  );

  useEffect(() => {
    trackSeoLandingViewed({
      audience: "homeowner",
      landing_type: "homepage",
      path: "/",
    });
  }, []);

  useEffect(() => {
    if (highRatedReviews.length > 0) {
      const interval = setInterval(() => {
        setCurrentReviewIndex(
          (prevIndex) => (prevIndex + 1) % highRatedReviews.length
        );
      }, 5000);
      return () => clearInterval(interval);
    } else {
      setCurrentReviewIndex(0);
    }
  }, [highRatedReviews]);

  const handleChatClick = () => {
    if (user && user.user_type === "contractor") {
      return;
    }
    navigate("/chat");
  };

  // The garden-render input in FeatureSections: lands in the chat with the
  // message auto-sent (?q=, same mechanism as the hero input).
  const handleDesignSubmit = (message) => {
    if (user && user.user_type === "contractor") {
      return;
    }
    trackCTAClicked("homepage_design_input", "chat");
    navigate(
      `/chat?q=${encodeURIComponent(message)}&source=homepage_design_input`
    );
  };

  const handleSignUpContractorClick = () => {
    trackContractorSignupStarted({ source: "homepage_contractor_cta" });
    navigate("/contractor-packages?source=homepage_contractor_cta");
  };

  const handleCallSignupClick = () => {
    trackCallSignupClicked({ source: "homepage_contractor_cta" });
  };

  if (
    user &&
    (profileLoading ||
      (user.user_type === "contractor" && !user.contractor_profile) ||
      (user.user_type === "homeowner" && !user.homeowner_profile))
  ) {
    return (
      <div className="loading-container">
        <p>Loading profile...</p>
      </div>
    );
  }

  return (
    <div className="home-container">
      <SEO
        title="Home Improvement Cost Estimates | Pricing Penguin"
        description="Explore a modelled budget for your home improvement project, clarify its scope and browse tradesperson profiles. Availability varies by trade and area."
        canonical="https://www.pricingpenguin.co.uk/"
      />
      <OrganizationSchema />
      <div className="home-content">
        <HeroSection onChatClick={handleChatClick} onContractorClick={handleSignUpContractorClick} />
        <HowItWorks />
        <FeatureSections
          onChatClick={handleChatClick}
          onDesignSubmit={handleDesignSubmit}
        />
        <PartnersSection />
        <ContractorSignUpSection
          onSignUpContractorClick={handleSignUpContractorClick}
          onCallSignupClick={handleCallSignupClick}
        />
        <PopularProjects />

        {/* Compact link to the day-rates hub — the full tradespeople
            directory moved to /day-rates to declutter the homepage. */}
        <div className="section">
          <Link to="/day-rates" className="home-hub-widget">
            <div className="home-hub-widget-icon">
              <i className="fas fa-hard-hat" aria-hidden="true" />
            </div>
            <div className="home-hub-widget-text">
              <span className="home-hub-widget-title">
                Trades People Day Rates 2026
              </span>
              <span className="home-hub-widget-sub">
                Explore indicative day rates for plumbers, electricians,
                builders and other UK trades in your area
              </span>
            </div>
            <span className="home-hub-widget-arrow" aria-hidden="true">
              <i className="fas fa-arrow-right" />
            </span>
          </Link>
        </div>

        <DidYouKnow />

        {reviewsError ? (
          <div className="section home-reviews">
            <h2 className="section-title">What Our Users Say</h2>
            <p role={isReviewsLoading ? "status" : "alert"}>
              {isReviewsLoading ? "Loading reviews..." : "We couldn't load reviews. Please try again."}
            </p>
            <button type="button" className="button button-secondary" onClick={retryReviews} disabled={isReviewsLoading}>
              Retry reviews
            </button>
          </div>
        ) : isReviewsLoading ? (
          <div className="section home-reviews">
            <h2 className="section-title">What Our Users Say</h2>
            <div
              style={{
                textAlign: "center",
                padding: "40px 20px",
                color: "#71717A",
              }}
            >
              <p role="status">Loading reviews...</p>
            </div>
          </div>
        ) : (
          <ReviewsSection
            reviews={highRatedReviews}
            currentReviewIndex={currentReviewIndex}
          />
        )}
      </div>
      <HowItWorksVideoButton />
    </div>
  );
};

export default Home;
